// src/user/ResetearPassword.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  InputAdornment,
} from "@mui/material";

// Icons
import LockIcon from "@mui/icons-material/Lock";

export const ResetearPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [nuevaPassword, setNuevaPassword] = useState("");
  const [confirmarPassword, setConfirmarPassword] = useState("");

  const [mensaje, setMensaje] = useState("");
  const [tokenValido, setTokenValido] = useState(false);
  const [verificandoToken, setVerificandoToken] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { setAuth } = useAuth();
  const navigate = useNavigate();

  const pwdMismatch = useMemo(() => {
    if (!nuevaPassword && !confirmarPassword) return false;
    return nuevaPassword !== confirmarPassword;
  }, [nuevaPassword, confirmarPassword]);

  // =========================
  // Verificar token
  // =========================
  useEffect(() => {
    let alive = true;

    const validar = async () => {
      if (!token) {
        if (!alive) return;
        setVerificandoToken(false);
        setTokenValido(false);
        setMensaje("Token no válido.");
        return;
      }

      setVerificandoToken(true);
      setMensaje("");

      try {
        const res = await fetch(
          `${Global.url}usuarios/password/reset/verify?token=${encodeURIComponent(token)}`
        );

        if (!alive) return;

        setTokenValido(res.ok);
        if (!res.ok) setMensaje("Enlace inválido o expirado.");
      } catch (error) {
        console.error("Error validando token:", error);
        if (!alive) return;
        setTokenValido(false);
        setMensaje("Error al validar el token.");
      } finally {
        if (alive) setVerificandoToken(false);
      }
    };

    validar();
    return () => {
      alive = false;
    };
  }, [token]);

  // =========================
  // Submit
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setMensaje("");

    if (!nuevaPassword || nuevaPassword.length < 8) {
      setMensaje("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    if (pwdMismatch) {
      setMensaje("Las contraseñas no coinciden.");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${Global.url}usuarios/password/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, nuevaPassword }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setMensaje(data?.message || "Error al establecer nueva contraseña.");
        return;
      }

      // ✅ Login automático tras reset
      if (data?.token) localStorage.setItem("token", data.token);
      if (data?.user) localStorage.setItem("user", JSON.stringify(data.user));

      if (data?.user) setAuth(data.user);

      setMensaje(data?.message || "Contraseña actualizada correctamente.");

      setTimeout(() => {
        navigate("/app/inicio", { replace: true });
      }, 900);
    } catch (error) {
      console.error("Error al cambiar la contraseña:", error);
      setMensaje("Error al establecer nueva contraseña.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer">
      <Stack spacing={2}>
        <Box className="pageHeader">
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Restablecer contraseña
          </Typography>

          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            Introduce tu nueva contraseña para completar el proceso.
          </Typography>

          {mensaje && (
            <Typography sx={{ mt: 0.5, fontWeight: 600 }} color="error">
              {mensaje}
            </Typography>
          )}
        </Box>

        <Box className="contentContainer contentContainer--formSingle">
          <Paper sx={{ p: 2 }}>
            {verificandoToken ? (
              <Typography sx={{ color: "text.secondary" }}>Verificando enlace…</Typography>
            ) : !token || !tokenValido ? (
              <Typography color="error">
                {mensaje || "Enlace inválido o expirado."}
              </Typography>
            ) : (
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField
                    type="password"
                    label="Nueva contraseña"
                    value={nuevaPassword}
                    onChange={(e) => setNuevaPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="Mínimo 8 caracteres"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    type="password"
                    label="Confirmar nueva contraseña"
                    value={confirmarPassword}
                    onChange={(e) => setConfirmarPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="Repite la contraseña"
                    error={pwdMismatch}
                    helperText={pwdMismatch ? "Las contraseñas no coinciden." : " "}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type="submit"
                    disabled={isSubmitting || pwdMismatch}
                    aria-busy={isSubmitting}
                    sx={{ height: 40 }}
                  >
                    {isSubmitting ? "Guardando..." : "Guardar nueva contraseña"}
                  </Button>
                </Stack>
              </Box>
            )}
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};
